import { createApi } from '@reduxjs/toolkit/query/react';
import { createAuthBaseQuery } from './baseQuery';
import { API_BASE_URL } from '../constants/api';

// TAT report — proxied through our backend (see
// backend/src/controllers/tatReportController.js).
//
// Backend scopes by client_id + (for doctor sessions) Doctor_ID
// server-side from the JWT, so the page only sends the date range.
//
// Response shape:
//   { status, meta: { fromDate, toDate, totalMatched, scopedTo }, data: [...rows] }
//
// Each row is the raw IT Dose record (BookingCentre, PanelCode,
// VisitNo, TestName, all the Date/Time fields and `*-Approval(hrs)` /
// `Booking-Approval(hrs)` / `INOutTAT` timing columns). The component
// derives KPIs + chart data on the client so we don't have to change
// the backend each time a tile gets added.
//
// `keepUnusedDataFor: 3600` — same hour-long client cache the sales
// API uses. The backend also caches the upstream call for an hour, so
// in practice each unique (fromDate, toDate) hits the IT Dose
// endpoint at most once per hour across the whole portal.

export const tatReportApi = createApi({
  reducerPath: 'tatReportApi',
  baseQuery: createAuthBaseQuery(API_BASE_URL),
  keepUnusedDataFor: 3600,
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  endpoints: (builder) => ({
    // Centre/partner-wide view — every visit billed against a client.
    // GET /api/portal/tat-report?fromDate=YYYY-MM-DD&toDate=YYYY-MM-DD
    // `refresh` is a counter the Refresh button bumps, not a boolean. It is
    // part of the query args ON PURPOSE: RTK Query caches per argument set,
    // so a new value is what makes it go back to the network at all —
    // refetch() alone would re-request, but the SERVER would still answer
    // from its own cache. The value rides along as ?refresh=N, which the
    // backend reads as "skip your cache this once".
    getTatReport: builder.query({
      query: ({ fromDate, toDate, refresh = 0 }) =>
        `/portal/tat-report?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`
        + (refresh ? `&refresh=${encodeURIComponent(refresh)}` : ''),
    }),
    // Doctor-scoped view — same client (from JWT), narrowed to records
    // with the given Doctor_ID. Mirrors the patient-reports
    // by-customer-doctor split: explicit `doctorId` in the URL so the
    // RTK cache keys cleanly per-doctor and the frontend never has to
    // worry about which user is signed in to read the right slice.
    // Backend still enforces JWT-based doctor scope on top of this
    // param, so a doctor can't widen scope by passing a different id.
    // GET /api/portal/tat-report?fromDate=...&toDate=...&doctorId=N
    getTatReportByCustomerAndDoctor: builder.query({
      query: ({ fromDate, toDate, doctorId, refresh = 0 }) =>
        `/portal/tat-report?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}&doctorId=${encodeURIComponent(doctorId)}`
        + (refresh ? `&refresh=${encodeURIComponent(refresh)}` : ''),
    }),
  }),
});

export const {
  useGetTatReportQuery,
  useGetTatReportByCustomerAndDoctorQuery,
} = tatReportApi;
